/* global anime */
import {
	getMaskedTextRevealStartY,
	prepareGradientTextAnimation,
	setLineTextMaskRevealClip,
} from './util'
import { getCharacterLevelAnimationTargets, LAYBUTTON_ANIMATION_ROOT_SELECTOR } from './text_animation_targets'
import {
	clearTypewriterBackgroundReveal,
	prepareTypewriterBackgroundReveal,
	setTypewriterBackgroundRevealProgress,
} from './typewriter_background_reveal'

export function taSplitLog() {}

export function taSplitLogTarget() {}

export function taResizeTrace() {}

export function taResizeTraceTarget() {}

function setMaskedTextRevealStartState(elements) {
	if (!elements || typeof elements.forEach !== 'function') {
		return
	}
	elements.forEach((el) => {
		anime.set(el, { translateY: getMaskedTextRevealStartY(el) })
	})
}

/** False while prepareTexts has not yet built the children the timeline
 *  targets. Per animation-type so we don't start a word/character timeline
 *  during the short frame where only line wrappers exist. */
export function isTextAnimationSplitDomReadyForPlay(target) {
	if (!target || !target.getAttribute) {
		return false
	}
	const animType = target.getAttribute('data-animation-type')
	if (!animType) {
		return false
	}
	if (animType === 'fade_whole_text' || animType === 'fade_and_slide_whole_text') {
		return true
	}
	if (
		animType === 'typewriter' ||
		animType === 'typewriter_fade' ||
		animType === 'masked_characters' ||
		animType === 'fade_characters' ||
		animType === 'fade_and_slide_characters'
	) {
		return !!target.querySelector('.lay-text-line .char')
	}
	if (
		animType === 'masked_words' ||
		animType === 'fade_words' ||
		animType === 'typewriter_words' ||
		animType === 'fade_and_slide_words'
	) {
		return !!target.querySelector('.lay-text-line .word')
	}
	// fade_lines / masked_lines / typewriter_lines / fade_and_slide_lines: line-level
	return !!target.querySelector('.lay-text-line')
}

/**
 * @param {WeakMap<Element, number[]>} typewriterTimeouts
 * @param {Element} target
 */
export function discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target) {
	const existingTimeouts = typewriterTimeouts.get(target)
	if (existingTimeouts) {
		existingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
		typewriterTimeouts.delete(target)
	}
	clearTypewriterBackgroundReveal(target)
}

/**
 * Same visual “before play” state as resetAnimationState (no timeline). Call synchronously right after
 * split DOM is inserted so there is no paint of full text (esp. when .lay-text-animation-initialized).
 */
export function applyPreRevealVisualStateToTarget(target) {
	if (!target) {
		return
	}
	target.classList.remove('lay-text-animation-initialized')
	prepareGradientTextAnimation(target)
	const animationType = target.getAttribute('data-animation-type')
	const lines = target.querySelectorAll('.lay-text-line')
	const words = target.querySelectorAll('.lay-text-line .word')
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')

	switch (animationType) {
		case 'masked_lines': {
			setMaskedTextRevealStartState(lines)
			break
		}
		case 'masked_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			setMaskedTextRevealStartState(words)
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { translateY: '0%' })
				})
				anime.set(b, { translateY: getMaskedTextRevealStartY(b) })
			})
			break
		}
		case 'masked_characters': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			setMaskedTextRevealStartState(characters)
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, { translateY: '0%' })
				})
				anime.set(b, { translateY: getMaskedTextRevealStartY(b) })
			})
			break
		}
		case 'typewriter_lines': {
			anime.set(lines, { opacity: 0 })
			break
		}
		case 'typewriter_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, { opacity: 0 })
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { opacity: 1 })
				})
				anime.set(b, { opacity: 0 })
			})
			break
		}
		case 'typewriter':
		case 'typewriter_fade': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(characters, {
				opacity: 0,
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, { opacity: 1 })
				})
				anime.set(b, { opacity: 0 })
			})
			break
		}
		case 'fade_whole_text':
		case 'fade_and_slide_whole_text': {
			break
		}
		case 'fade_lines': {
			anime.set(lines, { opacity: 0 })
			break
		}
		case 'fade_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, { opacity: 0 })
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { opacity: 1 })
				})
				anime.set(b, { opacity: 0 })
			})
			break
		}
		case 'fade_characters': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(characters, { opacity: 0 })
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, { opacity: 1 })
				})
				anime.set(b, { opacity: 0 })
			})
			break
		}
		// `fade_and_slide_*` — pre-reveal state is just `opacity: 0` on
		// the unit elements (lines / words / chars). The translate +
		// scale starting positions are applied by anime.js the moment
		// the timeline ticks the first frame; we don't need to set
		// them manually here because the unit is invisible anyway.
		case 'fade_and_slide_lines': {
			anime.set(lines, { opacity: 0 })
			break
		}
		case 'fade_and_slide_words': {
			anime.set(words, { opacity: 0 })
			break
		}
		case 'fade_and_slide_characters': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			anime.set(characters, { opacity: 0 })
			break
		}
		default: {
			break
		}
	}

	if (
		animationType === 'masked_lines' ||
		animationType === 'masked_words' ||
		animationType === 'masked_characters'
	) {
		setLineTextMaskRevealClip(lineMasks, true)
	}

	target.style.opacity = '0'
}

/** End state after a finished animation (used when DOM was rebuilt but we should not replay). */
export function snapTextAnimationToFinishedAppearance(target) {
	if (!target) {
		return
	}
	const animationType = target.getAttribute('data-animation-type')
	const lines = target.querySelectorAll('.lay-text-line')
	const words = target.querySelectorAll('.lay-text-line .word')
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')

	switch (animationType) {
		case 'masked_lines': {
			anime.set(lines, {
				translateY: '0%',
			})
			setLineTextMaskRevealClip(lineMasks, false)
			target.style.opacity = '1'
			break
		}
		case 'masked_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, {
				translateY: '0%',
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { translateY: '0%' })
				})
				anime.set(b, { translateY: '0%' })
			})
			setLineTextMaskRevealClip(lineMasks, false)
			target.style.opacity = '1'
			break
		}
		case 'masked_characters': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(characters, {
				translateY: '0%',
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, { translateY: '0%' })
				})
				anime.set(b, { translateY: '0%' })
			})
			setLineTextMaskRevealClip(lineMasks, false)
			target.style.opacity = '1'
			break
		}
		case 'fade_lines': {
			anime.set(lines, { opacity: 1 })
			target.style.opacity = '1'
			break
		}
		case 'fade_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, { opacity: 1 })
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { opacity: 1 })
				})
				anime.set(b, { opacity: 1 })
			})
			target.style.opacity = '1'
			if (animationType === 'typewriter' || animationType === 'typewriter_fade') {
				const typewriterUnits = getCharacterLevelAnimationTargets(target)
				prepareTypewriterBackgroundReveal(target, typewriterUnits, false)
				setTypewriterBackgroundRevealProgress(target, typewriterUnits.length, false)
			}
			break
		}
		case 'fade_characters':
		case 'typewriter':
		case 'typewriter_fade': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(characters, {
				opacity: 1,
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, { opacity: 1 })
				})
				anime.set(b, { opacity: 1 })
			})
			target.style.opacity = '1'
			break
		}
		case 'typewriter_lines': {
			anime.set(lines, { opacity: 1 })
			target.style.opacity = '1'
			break
		}
		case 'typewriter_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, { opacity: 1 })
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, { opacity: 1 })
				})
				anime.set(b, { opacity: 1 })
			})
			target.style.opacity = '1'
			break
		}
		case 'fade_whole_text':
		case 'fade_and_slide_whole_text': {
			target.style.opacity = '1'
			anime.set(target, {
				translateX: 0,
				translateY: 0,
				scale: 1,
				filter: 'blur(0px)',
			})
			break
		}
		case 'fade_and_slide_lines': {
			anime.set(lines, {
				opacity: 1,
				translateX: 0,
				translateY: 0,
				scale: 1,
				filter: 'blur(0px)',
			})
			target.style.opacity = '1'
			break
		}
		case 'fade_and_slide_words': {
			const btnRoots = new Set()
			words.forEach((w) => {
				const b = w.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(words, {
				opacity: 1,
				translateX: 0,
				translateY: 0,
				scale: 1,
				filter: 'blur(0px)',
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.word').forEach((w) => {
					anime.set(w, {
						opacity: 1,
						translateX: 0,
						translateY: 0,
						scale: 1,
						filter: 'blur(0px)',
					})
				})
				anime.set(b, {
					opacity: 1,
					translateX: 0,
					translateY: 0,
					scale: 1,
					filter: 'blur(0px)',
				})
			})
			target.style.opacity = '1'
			break
		}
		case 'fade_and_slide_characters': {
			const characters = target.querySelectorAll('.lay-text-line .char')
			const btnRoots = new Set()
			characters.forEach((c) => {
				const b = c.closest(LAYBUTTON_ANIMATION_ROOT_SELECTOR)
				if (b && target.contains(b)) {
					btnRoots.add(b)
				}
			})
			anime.set(characters, {
				opacity: 1,
				translateX: 0,
				translateY: 0,
				scale: 1,
				filter: 'blur(0px)',
			})
			btnRoots.forEach((b) => {
				b.querySelectorAll('.char').forEach((c) => {
					anime.set(c, {
						opacity: 1,
						translateX: 0,
						translateY: 0,
						scale: 1,
						filter: 'blur(0px)',
					})
				})
				anime.set(b, {
					opacity: 1,
					translateX: 0,
					translateY: 0,
					scale: 1,
					filter: 'blur(0px)',
				})
			})
			target.style.opacity = '1'
			break
		}
		default: {
			target.style.opacity = '1'
		}
	}
	target.classList.add('lay-text-animation-initialized')
}

/**
 * Pretext/resize replaced split DOM after timelines were dropped — mirror initObserver viewport rules.
 * @param {Element} target
 * @param {(el: Element) => void} playAnimationFn
 * @param {(el: Element, msg: string, extra?: object) => void} [debugFn]
 */
export function reapplyViewportAfterTextDomResplit(target, playAnimationFn, debugFn) {
	target.classList.remove('text-animation-ready-to-play')
	target.classList.remove('text-animation-in-view')
	target.classList.add('text-animation-never-played')

	const rect = target.getBoundingClientRect()
	if (rect.top > window.innerHeight) {
		target.classList.add('text-animation-ready-to-play')
		if (debugFn) {
			debugFn(target, 'after texts_are_split: below viewport, ready-to-play', { rectTop: rect.top })
		}
	} else if (rect.top >= 0) {
		if (debugFn) {
			debugFn(target, 'after texts_are_split: in viewport, replaying animation', { rectTop: rect.top })
		}
		playAnimationFn(target)
		target.classList.remove('text-animation-never-played')
		target.classList.add('text-animation-in-view')
	} else {
		if (debugFn) {
			debugFn(target, 'after texts_are_split: above viewport, waiting', { rectTop: rect.top })
		}
	}
}
